import React, { useState } from 'react'
import { ArrowRight, ArrowLeft, Globe, Info, AlertTriangle, Check, BarChart3 } from 'lucide-react'
import { parseCidr, isValidBaseCidr } from '../utils/cidrUtils'

const BASE_OPTIONS = [
  { value: '10.0.0.0/16', label: '10.0.0.0/16', desc: 'Most common' },
  { value: '172.16.0.0/16', label: '172.16.0.0/16', desc: 'Alternative' },
  { value: '192.168.0.0/16', label: '192.168.0.0/16', desc: 'Alternative' },
  { value: 'custom', label: 'Custom CIDR', desc: 'Enter your own' }
]

function Step3BaseCidr({ vcnCount, vcnNames, subnetCounts, initialBaseCidrs, useVlsm, onComplete, onBack }) {
  const [baseCidrs, setBaseCidrs] = useState(initialBaseCidrs)
  const [customCidrs, setCustomCidrs] = useState(initialBaseCidrs.map(b =>
    BASE_OPTIONS.some(opt => opt.value === b) ? '' : b
  ))
  const [errors, setErrors] = useState({})
  const [showSummary, setShowSummary] = useState(false)
  const [useFullBaseCidr, setUseFullBaseCidr] = useState(false)

  const handleBaseChange = (index, value) => {
    const newBaseCidrs = [...baseCidrs]
    newBaseCidrs[index] = value
    setBaseCidrs(newBaseCidrs)

    // Clear error for this index
    setErrors(prev => ({ ...prev, [index]: null }))

    // If not custom, also update the custom field
    if (value !== 'custom') {
      const newCustomCidrs = [...customCidrs]
      newCustomCidrs[index] = ''
      setCustomCidrs(newCustomCidrs)
    }
  }

  const handleCustomChange = (index, value) => {
    const newCustomCidrs = [...customCidrs]
    newCustomCidrs[index] = value
    setCustomCidrs(newCustomCidrs)

    // Validate the custom CIDR
    if (value.trim()) {
      const parsed = parseCidr(value.trim())
      if (!parsed) {
        setErrors(prev => ({ ...prev, [index]: 'Invalid CIDR format' }))
      } else if (!isValidBaseCidr(value.trim())) {
        setErrors(prev => ({ ...prev, [index]: 'Must be within 10.0.0.0/8, 172.16.0.0/12, or 192.168.0.0/16' }))
      } else if (parsed.prefix > 24) {
        setErrors(prev => ({ ...prev, [index]: 'Base CIDR prefix must be /16 to /24' }))
      } else {
        setErrors(prev => ({ ...prev, [index]: null }))
      }
    } else {
      setErrors(prev => ({ ...prev, [index]: 'Enter a CIDR' }))
    }
  }

  const getEffectiveBaseCidr = (index) => {
    if (baseCidrs[index] === 'custom') {
      return customCidrs[index]?.trim() || ''
    }
    return baseCidrs[index]
  }

  const validateAll = () => {
    const newErrors = {}
    const finalBaseCidrs = []

    for (let i = 0; i < vcnCount; i++) {
      const base = getEffectiveBaseCidr(i)

      if (!base) {
        newErrors[i] = baseCidrs[i] === 'custom' ? 'Enter a custom CIDR' : 'Select a base CIDR'
      } else {
        const parsed = parseCidr(base)
        if (!parsed) {
          newErrors[i] = 'Invalid CIDR format'
        } else if (!isValidBaseCidr(base)) {
          newErrors[i] = 'Must be within 10.0.0.0/8, 172.16.0.0/12, or 192.168.0.0/16'
        } else if (parsed.prefix > 24) {
          newErrors[i] = 'Base CIDR prefix must be /16 to /24'
        } else {
          finalBaseCidrs.push(base)
        }
      }
    }

    setErrors(newErrors)
    return { valid: Object.keys(newErrors).length === 0, finalBaseCidrs }
  }

  const handleContinue = () => {
    const { valid, finalBaseCidrs } = validateAll()
    if (valid) {
      onComplete(finalBaseCidrs, useFullBaseCidr)
    }
  }

  // Group VCNs by base CIDR for summary
  const baseGroups = {}
  for (let i = 0; i < vcnCount; i++) {
    const base = getEffectiveBaseCidr(i)
    if (base) {
      if (!baseGroups[base]) {
        baseGroups[base] = []
      }
      baseGroups[base].push(i)
    }
  }

  return (
    <div className="step-card">
      <div className="step-header">
        <h2>Select Base CIDR Range per VCN</h2>
        <p className="step-description">
          Choose the IP address range for each VCN. VCNs sharing the same base will have it split automatically.
        </p>
      </div>

      <div className="step-content">
        <div className="base-cidr-list">
          {Array.from({ length: vcnCount }, (_, i) => (
            <div key={i} className={`base-cidr-item ${errors[i] ? 'has-error' : ''}`}>
              <div className="vcn-info-row">
                <div className="vcn-badge">VCN {i + 1}</div>
                <span className="vcn-name">{vcnNames[i]}</span>
                <span className="subnet-badge">{subnetCounts[i]} subnet{subnetCounts[i] !== 1 ? 's' : ''}</span>
              </div>

              <div className="base-cidr-control">
                <label className="base-label">
                  <Globe size={16} />
                  Base CIDR Range
                </label>

                <select
                  className="base-select"
                  value={baseCidrs[i]}
                  onChange={(e) => handleBaseChange(i, e.target.value)}
                >
                  <option value="">Select a base range...</option>
                  {BASE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} — {opt.desc}
                    </option>
                  ))}
                </select>

                {baseCidrs[i] === 'custom' && (
                  <input
                    type="text"
                    className={`custom-base-input ${errors[i] ? 'input-error' : ''}`}
                    placeholder="e.g., 10.1.0.0/16"
                    value={customCidrs[i] || ''}
                    onChange={(e) => handleCustomChange(i, e.target.value)}
                  />
                )}

                {errors[i] && (
                  <div className="field-error">
                    <AlertTriangle size={14} />
                    {errors[i]}
                  </div>
                )}

                {baseCidrs[i] && baseCidrs[i] !== 'custom' && !errors[i] && (
                  <div className="field-success">
                    <Check size={14} />
                    Will use {baseCidrs[i]}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {Object.keys(baseGroups).length > 0 && (
          <div className="base-summary">
            <h4 onClick={() => setShowSummary(!showSummary)} className="summary-toggle">
              <Info size={16} />
              Base CIDR Allocation Summary
              {showSummary ? ' ▼' : ' ▶'}
            </h4>

            {showSummary && (
              <div className="summary-content">
                {Object.entries(baseGroups).map(([base, indices]) => (
                  <div key={base} className="summary-item">
                    <div className="summary-base">{base}</div>
                    <div className="summary-details">
                      {indices.length === 1 ? (
                        <span>Assigned to VCN {indices[0] + 1} only</span>
                      ) : (
                        <span>
                          Split between {indices.length} VCNs:
                          {indices.map(i => ` ${vcnNames[i]}`).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="info-box warning">
          <h3>How Base CIDR Splitting Works</h3>
          <p>
            When multiple VCNs share the same base CIDR, it will be automatically split
            into equal non-overlapping blocks. For example, if 2 VCNs share 10.0.0.0/16,
            they will each get a /17 block (10.0.0.0/17 and 10.0.128.0/17).
          </p>
        </div>

        {!useVlsm && (
          <div className="info-box full-base-cidr-option">
            <label className="full-base-checkbox-label">
              <input
                type="checkbox"
                className="full-base-checkbox"
                checked={useFullBaseCidr}
                onChange={(e) => setUseFullBaseCidr(e.target.checked)}
              />
              <div className="checkbox-content">
                <div className="checkbox-header">
                  <BarChart3 size={18} />
                  <strong>Use full base CIDR (show utilization statistics)</strong>
                </div>
                <p className="checkbox-description">
                  When enabled, the calculator will use the full base CIDR as the VCN CIDR instead of calculating an optimized/smaller size. This allows you to see utilization statistics showing how much of the base CIDR is being used by subnets versus wasted space.
                </p>
              </div>
            </label>
          </div>
        )}
      </div>

      <div className="step-footer">
        <button className="btn btn-secondary" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button className="btn btn-primary btn-large" onClick={handleContinue}>
          Generate CIDR Plan
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  )
}

export default Step3BaseCidr
